import React, { FC, HTMLAttributes, ReactNode, useEffect, useId, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "../../../icon/Icon";
import useAsideStatus from "../../../../hooks/useAsideStatus";
import themeConfig from "../../../../config/theme.config";
import Avatar from "../../../ui/Avatar";
import Tooltip from "../../../ui/Tooltip";

const navItemClasses = {
  default: classNames(
    "mb-2 p-3",
    "flex items-center",
    "cursor-pointer",
    "overflow-hidden",
    "rounded-xl",
    "border",
    "text-zinc-400",
    "hover:text-zinc-950 dark:hover:text-zinc-100",
    "grow",
    themeConfig.transition
  ),
  inactive: "border-transparent",
  active: "border-[#FEBA06] text-zinc-950 dark:border-[#FEBA06] dark:text-zinc-100",
  here: "text-zinc-950 dark:text-zinc-100 border-transparent",
};

const navItemChildCheck = (children) => {
  return children?.length > 1
    ? children?.map((child) => child.type.displayName).includes("NavButton")
    : children?.type?.displayName === "NavButton";
};

const NavItemText = (props) => {
  const { children, className, ...rest } = props;

  return (
    <div
      data-component-name="Nav/NavItemText"
      className={classNames("overflow-hidden truncate whitespace-nowrap", className)}
      {...rest}
    >
      {children}
    </div>
  );
};
NavItemText.displayName = "NavItemText";

export const NavItemContent = (props) => {
  const { children, className, ...rest } = props;

  const { asideStatus } = useAsideStatus();

  return (
    <div
      data-component-name="Nav/NavItemContent"
      className={classNames(
        "flex w-full items-center justify-between truncate",
        {
          hidden: !asideStatus,
        },
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};
NavItemContent.displayName = "NavItemContent";

const NavIcon = (props) => {
  const { className, icon = "HeroMinus" } = props;

  const { asideStatus } = useAsideStatus();

  return (
    <Icon
      data-component-name="Nav/NavIcon"
      icon={icon}
      className={classNames(
        "w-6 flex-none text-2xl",
        {
          "me-3": asideStatus,
        },
        className
      )}
    />
  );
};
NavIcon.displayName = "NavIcon";

export const NavButton = (props) => {
  const { icon, iconColor, className, iconClassName, ...rest } = props;

  return (
    <button data-component-name="Nav/NavButton" type="button" className={classNames(className)} {...rest}>
      <Icon
        icon={icon}
        color={iconColor}
        size="text-2xl"
        className={classNames(
          {
            "text-zinc-500 hover:text-zinc-950 dark:hover:text-zinc-100": !iconColor,
          },
          themeConfig.transition,
          iconClassName
        )}
      />
    </button>
  );
};
NavButton.displayName = "NavButton";

export const NavItem = (props) => {
  const { children, icon, text, to, className, ...rest } = props;

  const { t } = useTranslation("menu");

  const { asideStatus, setAsideStatus } = useAsideStatus();

  const isChildrenNavButton = navItemChildCheck(children);

  const CONTENT = (
    <>
      <NavIcon icon={icon} />
      <NavItemContent>
        <NavItemText>{t(text)}</NavItemText>
        {children && !isChildrenNavButton && <div>{children}</div>}
      </NavItemContent>
    </>
  );

  return (
    <Tooltip text={asideStatus ? "" : t(text)} placement="right">
      <li
        data-component-name="Nav/NavItem"
        className={classNames("flex list-none items-center overflow-hidden whitespace-nowrap", className)}
        {...rest}
      >
        {to ? (
          <>
            {/* For Desktop */}
            <NavLink
              end
              to={to}
              className={({ isActive }) =>
                isActive
                  ? classNames(navItemClasses.default, navItemClasses.active, "max-md:hidden")
                  : classNames(navItemClasses.default, navItemClasses.inactive, "max-md:hidden")
              }
            >
              {CONTENT}
            </NavLink>
            {/* For Mobile */}
            <NavLink
              end
              to={to}
              onClick={() => setAsideStatus(false)}
              className={({ isActive }) =>
                isActive
                  ? classNames(navItemClasses.default, navItemClasses.active, "md:hidden")
                  : classNames(navItemClasses.default, navItemClasses.inactive, "md:hidden")
              }
            >
              {CONTENT}
            </NavLink>
          </>
        ) : (
          <>
            {/* For Desktop */}
            <div className={classNames(navItemClasses.default, navItemClasses.inactive, "max-md:hidden")}>
              {CONTENT}
            </div>
            {/* For Mobile */}
            <div className={classNames(navItemClasses.default, navItemClasses.inactive, "md:hidden")}>{CONTENT}</div>
          </>
        )}
        {asideStatus && children && isChildrenNavButton && (
          <div className="mb-2 flex items-center gap-3 px-3">{children}</div>
        )}
      </li>
    </Tooltip>
  );
};
NavItem.displayName = "NavItem";

export const NavCollapse = (props) => {
  const { children, icon, text, className, to, ...rest } = props;

  const { t } = useTranslation("menu");

  const id = useId();
  const [isActive, setIsActive] = useState(false);

  const { asideStatus } = useAsideStatus();

  const location = useLocation();
  const here = to !== "/" && location.pathname.includes(to);

  useEffect(() => {
    setIsActive(here);
  }, [here, location.pathname]);

  return (
    <li data-component-name="Nav/NavCollapse" className={classNames("list-none overflow-hidden", className)} {...rest}>
      <Tooltip text={asideStatus ? "" : t(text)} placement="right">
        <div
          role="presentation"
          className={
            isActive || here
              ? classNames(navItemClasses.default, navItemClasses.here)
              : classNames(navItemClasses.default, navItemClasses.inactive)
          }
          onClick={() => setIsActive(!isActive)}
        >
          <NavIcon icon={icon} />

          <NavItemContent>
            <NavItemText>{t(text)}</NavItemText>
            <div>
              <Icon
                icon="HeroChevronDown"
                className={classNames(
                  "text-2xl",
                  {
                    "rotate-180": isActive,
                  },
                  themeConfig.transition
                )}
              />
            </div>
          </NavItemContent>
        </div>
      </Tooltip>
      <AnimatePresence>
        {isActive && (
          <motion.ul
            key={id}
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { height: "auto" },
              collapsed: { height: 0 },
            }}
            transition={{ duration: 0.3 }}
            className={classNames("!transition-margin !duration-300 !ease-in-out", {
              "ms-4": asideStatus,
            })}
          >
            {children}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
};
NavCollapse.displayName = "NavCollapse";

export const NavTitle = (props) => {
  const { children, className, ...rest } = props;

  const { t } = useTranslation("menu");

  const { asideStatus } = useAsideStatus();

  return (
    <Tooltip text={asideStatus ? "" : t(children)} placement="right">
      <li
        data-component-name="Nav/NavTitle"
        className={classNames(
          "list-none overflow-hidden truncate whitespace-nowrap px-3 py-1.5 text-sm font-semibold text-zinc-500",
          className
        )}
        {...rest}
      >
        {asideStatus ? children : <div className="my-1.5 h-2 w-full max-w-[6rem] rounded-full bg-zinc-500" />}
      </li>
    </Tooltip>
  );
};
NavTitle.displayName = "NavTitle";

export const NavUser = (props) => {
  const { children, image, text, to, className, ...rest } = props;

  const { t } = useTranslation("menu");

  const { asideStatus, setAsideStatus } = useAsideStatus();

  const isChildrenNavButton = navItemChildCheck(children);

  const CONTENT = (
    <>
      <Avatar
        src={image}
        name={text}
        className={classNames("w-6 rounded-full", {
          "me-3": asideStatus,
        })}
        rounded="rounded"
      />
      <NavItemContent>
        <NavItemText>{t(text)}</NavItemText>
        {children && !isChildrenNavButton && <div>{children}</div>}
      </NavItemContent>
    </>
  );

  return (
    <Tooltip text={asideStatus ? "" : t(text)} placement="right">
      <li
        data-component-name="Nav/NavUser"
        className={classNames("flex list-none items-center overflow-hidden whitespace-nowrap", className)}
        {...rest}
      >
        {to ? (
          <>
            {/* For Desktop */}
            <NavLink
              end
              to={to}
              className={({ isActive }) =>
                isActive
                  ? classNames(navItemClasses.default, navItemClasses.active, "max-md:hidden")
                  : classNames(navItemClasses.default, navItemClasses.inactive, "max-md:hidden")
              }
            >
              {CONTENT}
            </NavLink>
            {/* For Mobile */}
            <NavLink
              end
              to={to}
              onClick={() => setAsideStatus(false)}
              className={({ isActive }) =>
                isActive
                  ? classNames(navItemClasses.default, navItemClasses.active, "md:hidden")
                  : classNames(navItemClasses.default, navItemClasses.inactive, "md:hidden")
              }
            >
              {CONTENT}
            </NavLink>
          </>
        ) : (
          <>
            {/* For Desktop */}
            <div className={classNames(navItemClasses.default, navItemClasses.inactive, "max-md:hidden")}>
              {CONTENT}
            </div>
            {/* For Mobile */}
            <div className={classNames(navItemClasses.default, navItemClasses.inactive, "md:hidden")}>{CONTENT}</div>
          </>
        )}
        {asideStatus && children && isChildrenNavButton && (
          <div className="mb-2 flex items-center gap-3 px-3">{children}</div>
        )}
      </li>
    </Tooltip>
  );
};
NavUser.displayName = "NavUser";

export const NavSeparator = (props) => {
  const { className, ...rest } = props;
  return (
    <li
      data-component-name="Nav/NavSeparator"
      className={classNames("mb-2 list-none rounded-full border-b border-zinc-500/25", className)}
      {...rest}
    />
  );
};
NavSeparator.displayName = "NavSeparator";

const Nav = (props) => {
  const { children, className, ...rest } = props;

  return (
    <nav data-component-name="Nav" className={classNames(className)} {...rest}>
      <ul>{children}</ul>
    </nav>
  );
};
Nav.displayName = "Nav";

export default Nav;
